import { Trans, useLingui } from '@lingui/react/macro';
import { GetApiAuditLogResponse, useUserDateFormat } from '@sit/client-shared';
import PAGE_SIZES from '@web/constants/pageSizes';
import { dateWithFallback, stringWithFallback, timeWithFallback } from '@web/helpers/label';
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from 'carbon-react/lib/components/flat-table';
import Pager from 'carbon-react/lib/components/pager';
import Typography from 'carbon-react/lib/components/typography';
import styled from 'styled-components';

const TableBox = styled.div`
  margin-bottom: ${(props) => props.theme.spacing}px;
  width: 100%;

  & [data-role="flat-table-container"] {
    padding-bottom: ${(props) => props.theme.spacing * 2}px;
  }

  & [data-component="flat-table-body"] > tr:last-child > td:first-child {
    border-bottom-left-radius: ${(props) => props.theme.spacing}px;
  }

  & [data-component="flat-table-body"] > tr:last-child > td:last-child {
    border-bottom-right-radius: ${(props) => props.theme.spacing}px;
  }
`;

interface AuditTableProps {
  data: GetApiAuditLogResponse | undefined;
  loading?: boolean;
  page: number;
  pageSize: number;
  onPaginate: (currentPage: number, pageSize: number, origin: string) => void;
}

function useTableEntries(entries: GetApiAuditLogResponse['entries']) {
  const dateFormat = useUserDateFormat();
  const { t } = useLingui();

  const localizeAction = (value: string | undefined | null): string => {
    switch (value) {
      case 'create':
        return t`Created`;
      case 'update':
        return t`Updated`;
      default:
        return stringWithFallback(value);
    }
  };

  const localizeSource = (value: string | undefined | null): string => {
    switch (value) {
      case 'api':
        return t`API`;
      case 'ui':
        return t`UI`;
      case 'mobile':
        return t`Mobile`;
      default:
        return stringWithFallback(value);
    }
  };

  return entries.map((entry) => {
    const timestamp = new Date(entry.timestamp);

    return {
      ...entry,
      key: entry.timestamp,
      timestamp: `${dateWithFallback(timestamp, dateFormat)} ${timeWithFallback(timestamp)}`,
      action: localizeAction(entry.action),
      user: stringWithFallback(entry.user),
      source: localizeSource(entry.source),
    };
  });
}

function AuditTable({ data, loading = false, page, onPaginate }: AuditTableProps) {
  const tableEntries = useTableEntries(data?.entries ?? []);
  return (
    <>
      <TableBox>
        <FlatTable colorTheme="transparent-white" width="100%" overflowX="auto">
          <FlatTableHead>
            <FlatTableRow>
              <FlatTableHeader width={180}>
                <Trans>Date</Trans>
              </FlatTableHeader>
              <FlatTableHeader>
                <Trans>Action</Trans>
              </FlatTableHeader>
              <FlatTableHeader width={200}>
                <Trans>Completed by</Trans>
              </FlatTableHeader>
              <FlatTableHeader>
                <Trans>Source</Trans>
              </FlatTableHeader>
              <FlatTableHeader>
                <Trans>Field</Trans>
              </FlatTableHeader>
              <FlatTableHeader>
                <Trans>Before</Trans>
              </FlatTableHeader>
              <FlatTableHeader>
                <Trans>After</Trans>
              </FlatTableHeader>
            </FlatTableRow>
          </FlatTableHead>
          <FlatTableBody>
            {tableEntries.map((entry) => (
              <FlatTableRow key={entry.key}>
                <FlatTableCell>
                  <Typography mb={0}>{entry.timestamp}</Typography>
                </FlatTableCell>
                <FlatTableCell>
                  <Typography mb={0}>{entry.action}</Typography>
                </FlatTableCell>
                <FlatTableCell>
                  <Typography mb={0}>{entry.user}</Typography>
                </FlatTableCell>
                <FlatTableCell>
                  <Typography mb={0}>{entry.source}</Typography>
                </FlatTableCell>
                <FlatTableCell>
                  <Typography mb={0}>{entry.fieldName}</Typography>
                </FlatTableCell>
                <FlatTableCell>
                  <Typography whiteSpace="nowrap" mb={0}>
                    {entry.fieldBefore}
                  </Typography>
                </FlatTableCell>
                <FlatTableCell>
                  <Typography whiteSpace="nowrap" mb={0}>
                    {entry.fieldAfter}
                  </Typography>
                </FlatTableCell>
              </FlatTableRow>
            ))}
            {tableEntries.length === 0 ? (
              loading ? (
                <FlatTableRow>
                  <FlatTableCell colspan={7}>
                    <Typography textAlign="center" mb={0}>
                      <Trans>Loading...</Trans>
                    </Typography>
                  </FlatTableCell>
                </FlatTableRow>
              ) : (
                <FlatTableRow>
                  <FlatTableCell colspan={7}>
                    <Typography textAlign="center" mb={0}>
                      <Trans>No results</Trans>
                    </Typography>
                  </FlatTableCell>
                </FlatTableRow>
              )
            ) : null}
          </FlatTableBody>
        </FlatTable>
      </TableBox>
      <Pager
        totalRecords={data?.totalCount}
        showPageSizeSelection
        pageSize={10}
        currentPage={page}
        onPagination={onPaginate}
        // @ts-expect-error it's OK that it's not mutable, the component does not mutate
        pageSizeSelectionOptions={PAGE_SIZES}
      />
    </>
  );
}

export default AuditTable;
